import { useEffect, useMemo, useState } from 'react';

import { Button } from '@bambi/component-library/src/components/Button/Button';

import { DispatchTripRead } from 'src/common/external/bambi-api/bambiApi';
import { ModalFooter } from 'src/common/primitives/Modal/ModalFooter';
import { VerticalSortableDragAndDropList } from 'src/common/SortableDragAndDropList/SortableDragAndDropList';
import { MultiloadIcon } from 'src/features/dispatch/multi-loading/MultiloadIcon';
import { MultiloadPickupDropoffOrderItem } from 'src/features/dispatch/multi-loading/MultiloadOrderStep';
import { orderMultiloadItems } from 'src/features/dispatch/multi-loading/useMultiloadOrderItems';
import { TripCardInternal } from 'src/features/dispatch/trips/TripCard/TripCard';

import { RouteTripTemplateCard } from './components/RouteTripTemplateCard';
import {
  MultiloadPickupDropoffTripTemplateOrderItem,
  RouteMultiloadTemplateFormValues,
  RouteTripTemplateFormValues,
} from './types';

// There is a lot of casting happening here because we're relying on the
// multi-load ordering hooks and components. In practice the types are mostly
// compatible, but this may lead to issues in the future.

interface RouteTemplateMultiloadTripFormProps {
  multiloadTripTemplate?: RouteMultiloadTemplateFormValues | undefined;
  tripTemplates: Array<RouteTripTemplateFormValues & { __id: string }>;
  skipSelect?: boolean;
  onCancel: () => void;
  onSubmit: (
    orderedMultiLoadItems: MultiloadPickupDropoffTripTemplateOrderItem[],
    selectedTripTemplates: Array<RouteTripTemplateFormValues & { __id: string }>
  ) => void;
}
export function RouteTemplateMultiloadTripForm({
  multiloadTripTemplate,
  tripTemplates,
  skipSelect = false,
  onCancel,
  onSubmit,
}: RouteTemplateMultiloadTripFormProps) {
  const isEditing = !!multiloadTripTemplate?.configuration.id;
  const [selectedTripsMap, setSelectedTripsMap] = useState<
    Record<string, boolean>
  >({});
  const [showOrderingView, setShowOrderingView] = useState(skipSelect);
  const [orderedMultiLoadItems, setOrderedMultiLoadItems] = useState<
    MultiloadPickupDropoffOrderItem[]
  >([]);

  useEffect(() => {
    if (skipSelect) {
      const selectionMap = tripTemplates.reduce((acc, tripTemplate) => {
        acc[tripTemplate.__id] = true;
        return acc;
      }, {} as Record<string, boolean>);

      setSelectedTripsMap(selectionMap);
    }
  }, [skipSelect, tripTemplates]);

  const selectedTripTemplates = useMemo(
    () =>
      tripTemplates.filter(
        (tripTemplate) => selectedTripsMap[tripTemplate.__id]
      ),
    [selectedTripsMap, tripTemplates]
  );

  useEffect(() => {
    if (skipSelect) {
      setOrderedMultiLoadItems(
        selectedTripTemplates as unknown as MultiloadPickupDropoffOrderItem[]
      );
      return;
    }

    setOrderedMultiLoadItems(
      // Casting because tripTemplates is close enough to DispatchTripRead for the
      // purpose of this function
      orderMultiloadItems(
        selectedTripTemplates as unknown as DispatchTripRead[],
        '__id'
      )
    );
  }, [selectedTripsMap, selectedTripTemplates, skipSelect]);

  return (
    <div className="flex flex-col gap-4">
      <div className="text-sm text-gray-500">
        Select the trips you want to multi-load together
      </div>
      {showOrderingView ? (
        <div className="flex flex-col gap-5">
          <div className="mt-3 flex justify-center">
            <MultiloadIcon />
          </div>
          <h2 className="-mt-2 text-center text-xl">
            Drag and drop to reorder
          </h2>
          <div className="mt-2 max-h-[50vh] overflow-auto">
            <VerticalSortableDragAndDropList
              items={orderedMultiLoadItems}
              renderItem={(orderItem) => (
                <TripCardInternal
                  key={orderItem.trip.id}
                  trip={orderItem.trip}
                  variant={orderItem.type}
                  hideMenu
                />
              )}
              onReorder={setOrderedMultiLoadItems}
            />
          </div>
          <ModalFooter>
            <Button onClick={onCancel}>Back</Button>
            <Button
              variant="primary"
              onClick={() =>
                onSubmit(
                  orderedMultiLoadItems as unknown as MultiloadPickupDropoffTripTemplateOrderItem[],
                  selectedTripTemplates
                )
              }
            >
              {isEditing
                ? 'Update multi-load template'
                : 'Create multi-load template'}
            </Button>
          </ModalFooter>
        </div>
      ) : (
        <>
          <div className="flex max-h-[45vh] flex-col gap-2 overflow-auto">
            {tripTemplates.map((tripTemplate) => {
              const isSelected = selectedTripsMap[tripTemplate.__id];
              return (
                <RouteTripTemplateCard
                  key={tripTemplate.__id}
                  tripTemplate={tripTemplate}
                  canSelect
                  isSelected={isSelected}
                  onSelect={() => {
                    setSelectedTripsMap((current) => ({
                      ...current,
                      [tripTemplate.__id]: !isSelected,
                    }));
                  }}
                />
              );
            })}
          </div>

          <ModalFooter>
            <Button onClick={onCancel}>Back</Button>
            <Button
              variant="primary"
              disabled={
                Object.values(selectedTripsMap).filter(Boolean).length === 0
              }
              onClick={() => setShowOrderingView(true)}
            >
              Order the trips
            </Button>
          </ModalFooter>
        </>
      )}
    </div>
  );
}
